import { UserState } from "interfaces";
import { useEffect } from "react";
import { FaCheck } from "react-icons/fa";
import { RootStateOrAny, useSelector } from "react-redux";
import { useListarUsuariosPorEquipe } from "services/api/Identity_Usuario/listar-usuarios-por-equipe";
import { AuthState } from "store/ducks/auth/types";
import styled from "styled-components";

type Corretor = {
  id: string;
  nome: string;
};

interface CorretoresSelectorProps {
  corretoresSelecionados: string[];
  setCorretoresSelecionados: (corretores: string[]) => void;
}

const corretores: Corretor[] = [
  { id: "1", nome: "Aldair Correa" },
  { id: "2", nome: "Danile Pereira" },
  { id: "3", nome: "Elena Silva" },
  { id: "4", nome: "Jorge" },
  { id: "5", nome: "Kevin Sleiter" },
  { id: "6", nome: "Marta Figueira" },
  { id: "7", nome: "Miguel Andrade" },
  { id: "8", nome: "Rodri Muller" },
  { id: "9", nome: "Sheila Matos" },
  { id: "10", nome: "Sofia Torres" },
  { id: "11", nome: "Zulmira Feliciano" },
];

const CorretoresSelector = ({
  corretoresSelecionados,
  setCorretoresSelecionados,
}: CorretoresSelectorProps) => {
  const { name, userId } = useSelector<RootStateOrAny, UserState & AuthState>(
    (state) => state.user
  );

  const { data: dataCorretores, refetch: refetchCorretores } =
    useListarUsuariosPorEquipe({
      queryKey: "listar-usuarios-por-equipe",
      IdUsuarioLider: [userId ?? ""],
    });

  useEffect(() => {
    refetchCorretores();
  }, []);

  const corretores = dataCorretores?.data || [];

  const toggleCorretor = (id: string) => {
    const novosCorretores = corretoresSelecionados?.includes(id)
      ? corretoresSelecionados.filter((item) => item !== id)
      : [...corretoresSelecionados, id];
    setCorretoresSelecionados(novosCorretores);
  };

  const toggleTodos = () => {
    if (corretoresSelecionados?.length === corretores?.length) {
      setCorretoresSelecionados([]);
    } else {
      setCorretoresSelecionados(corretores?.map((c: any) => c.idCorretor));
    }
  };

  const limparSelecao = () => {
    setCorretoresSelecionados([]);
  };

  const allSelected = corretoresSelecionados?.length === corretores?.length;

  return (
    <Container>
      <Header>
        <TextStrong>Equipe: </TextStrong>
        <EquipeText>{name}</EquipeText>
      </Header>

      <CorretoresHeader>
        <TextStrong>Corretores</TextStrong>
        <ContainerRightList>
          <TextStrong>
            {corretoresSelecionados?.length} selecionado
            {corretoresSelecionados?.length !== 1 ? "s" : ""}
          </TextStrong>
          <LimparSelecaoLink onClick={limparSelecao}>
            Limpar seleção
          </LimparSelecaoLink>
        </ContainerRightList>
      </CorretoresHeader>

      <CorretorList>
        <Option onClick={toggleTodos}>
          <Checkbox isChecked={allSelected}>
            {allSelected && <CheckIcon />}
          </Checkbox>
          <ListTitle>Todos</ListTitle>
        </Option>

        {corretores?.map((corretor: any) => {
          const isSelected = corretoresSelecionados.includes(
            corretor.idCorretor
          );
          return (
            <Option
              key={corretor.idCorretor}
              onClick={() => toggleCorretor(corretor.idCorretor)}
            >
              <Checkbox isChecked={isSelected}>
                {isSelected && <CheckIcon />}
              </Checkbox>
              <ListTitle>{corretor.apelido}</ListTitle>
            </Option>
          );
        })}
      </CorretorList>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const Header = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const EquipeText = styled.p`
  font-family: Inter;
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  line-height: 150%;
  letter-spacing: 0%;
  color: #424248;
`;

export const TextStrong = styled.h1`
  font-family: Inter;
  font-weight: 600;
  font-style: Semi Bold;
  font-size: 16px;
  line-height: 130%;
  letter-spacing: 0%;
  color: #424248;
`;

const CorretoresHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  justify-content: space-between;
`;

const ListTitle = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: #4a4a4a;
`;

const LimparSelecaoLink = styled.button`
  background: none;
  border: none;
  color: #4361ee;
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 150%;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin-left: auto;
`;

const CorretorList = styled.div`
  background-color: white;
  border: 1px solid #d5d5d5;
  padding: 16px 0;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
`;

const ContainerRightList = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 16px;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f9f9f9;
  }
`;

const Checkbox = styled.div<{ isChecked: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #d5d5d5;
  border-radius: 4px;
  margin-right: 12px;
  background-color: ${({ isChecked }) =>
    isChecked ? "#4361ee" : "transparent"};
  border-color: ${({ isChecked }) => (isChecked ? "#4361ee" : "#d5d5d5")};

  svg {
    width: 12px;
    height: 12px;
    color: white;
  }
`;

const CheckIcon = styled(FaCheck)`
  width: 12px;
  height: 12px;
  color: white;
`;

export default CorretoresSelector;
